import { useEffect, useState } from "react";
import { useScopeId } from "@core/hooks/useRouteParamId";
import {
  fetchPredefinedOptionListPreview,
  fetchPredefinedOptionLists,
} from "../api/kpiApi";
import type {
  PredefinedOptionList,
  PredefinedOptionListPreview,
} from "../types/predefinedOptionList";

/** The lists are a small fixed set: load once per nation and share between editors. */
const listCache = new Map<number, Promise<PredefinedOptionList[]>>();
const previewCache = new Map<string, Promise<PredefinedOptionListPreview | null>>();

function loadLists(nationId: number): Promise<PredefinedOptionList[]> {
  let pending = listCache.get(nationId);
  if (!pending) {
    pending = fetchPredefinedOptionLists(nationId).then((res) => {
      if (!res.isSuccess()) {
        listCache.delete(nationId);
        return [];
      }
      return res.getData()?.data ?? [];
    });
    listCache.set(nationId, pending);
  }
  return pending;
}

function loadPreview(nationId: number, code: string): Promise<PredefinedOptionListPreview | null> {
  const key = `${nationId}:${code}`;
  let pending = previewCache.get(key);
  if (!pending) {
    pending = fetchPredefinedOptionListPreview(nationId, code).then((res) => {
      if (!res.isSuccess()) {
        previewCache.delete(key);
        return null;
      }
      return res.getData()?.data ?? null;
    });
    previewCache.set(key, pending);
  }
  return pending;
}

/** Test helper: forget cached lists and previews. */
export function clearPredefinedOptionListCache(): void {
  listCache.clear();
  previewCache.clear();
}

/** All predefined option lists for the current nation. */
export function usePredefinedOptionLists(enabled = true) {
  const nationId = useScopeId() ?? 0;
  const [lists, setLists] = useState<PredefinedOptionList[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !nationId) return;
    let cancelled = false;
    setLoading(true);
    loadLists(nationId)
      .then((items) => {
        if (!cancelled) setLists(items);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, nationId]);

  return { lists, loading };
}

/** Read-only preview of one list (null code = nothing to preview). */
export function usePredefinedOptionListPreview(code: string | null | undefined) {
  const nationId = useScopeId() ?? 0;
  const [preview, setPreview] = useState<PredefinedOptionListPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPreview(null);
    setFailed(false);
    if (!code || !nationId) return;
    let cancelled = false;
    setLoading(true);
    loadPreview(nationId, code)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setFailed(data === null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code, nationId]);

  return { preview, loading, failed };
}
